import { useEffect, useState } from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useLicenseHook } from '../../Hooks/License/licenseHooks';

import { IconShoppingCart, IconKey, IconEye, IconEdit, IconTrash, IconFileDollar } from '@tabler/icons-react';

import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';
import RouteHelper from '../../Utilities/Helpers/RouteHelper';
import StatusHelper from '../../Utilities/Helpers/StatusHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';
import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { LicenseListField, DetailsInvoiceField, AuthenticationField } from '../../Utilities/Enums/ParametersEnum';
import { LicenseStatus, LicenseStatusEnum } from "../../Utilities/Enums/StatusEnums";
import { AdminModules } from '../../Utilities/Enums/AdminModulesEnum';
import { ValidityType } from '../../Utilities/Enums/ValidityTypeEnums';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';
import { RoleTypeEnum } from '../../Utilities/Enums/RolesEnums';

import { LicenseList } from '../../Models/License/License';
import { Authentication } from "../../Models/Authentication/Authentication";

import CustomListComponent from '../../CustomViews/Pages/ListComponent/CustomListComponent';
import CustomIconButton from '../../CustomViews/Elements/CustomIconButton';
import CustomDeleteConfirmation from '../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import CustomButton from '../../CustomViews/Elements/CustomButton';
import LicenseKeyTitle from '../../CustomViews/Pages/CustomTitles/LicenseKeyTitle';
import { TableContainer } from '../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';
import CustomListHeader from '../../CustomViews/Pages/ListComponent/CustomListHeader';
import CustomListSearch from '../../CustomViews/Pages/ListComponent/Search/CustomListSearch';
import { LegendItemInterface } from '../../Models/Common/List/LegendInterface';
import FormDatePicker from '../../CustomViews/Elements/FormDatePicker';
import FormDateRangePicker from '../../CustomViews/Elements/FormDateRangePicker';
import { CustomListSearchResetBtns } from '../../CustomViews/Pages/ListComponent/Search/CustomListSearchResetBtns';
import CustomExpirationDate from '../../CustomViews/Elements/CustomExpirationDate';
import GeneratedLicenseInvoiceModal from './GeneratedLicenseInvoiceModal';
import VendorsDropdown from '../../CustomViews/Elements/AutoComplete/VendorsDropdown';

export default function LicensesList() {
    const navigate = useNavigate();
    const location = useLocation()
    let _userData: Authentication = LocalStorageHelper.getUserData();

    //search params
    const queryParams = new URLSearchParams(location.search);
    const [searchParams, setSearchParams] = useSearchParams(queryParams);

    //use states
    let [openDelete, setopenDelete] = useState(false);
    let [toDeleteItem, settoDeleteItem] = useState<any>(null);
    let [generatedInvoicePublicId, setgeneratedInvoicePublicId] = useState<any>(null);
    let [generatedInvoiceNo, setgeneratedInvoiceNo] = useState<any>(null);
    let [openGeneratedInvoiceModal, setopenGeneratedInvoiceModal] = useState<boolean>(false);
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        status: QueryBuilderHelper.DefaultQueryFilterValue('status', 0) ?? 0,
        issueDateFrom: QueryBuilderHelper.DefaultQueryFilterValue('issueDateFrom', null),
        issueDateTo: QueryBuilderHelper.DefaultQueryFilterValue('issueDateTo', null),
        expiryDateFrom: QueryBuilderHelper.DefaultQueryFilterValue('expiryDateFrom', null),
        expiryDateTo: QueryBuilderHelper.DefaultQueryFilterValue('expiryDateTo', null),
        sortBy: QueryBuilderHelper.DefaultSortValue(LicenseListField.CREATED_DATE),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
        vendorPublicId: QueryBuilderHelper.DefaultQueryFilterValue('vendorPublicId', null),
    });

    const q_queryKey = [CachingEnum.LICENSES, queryStr]
    const { data, fetchStatus, generateLicenseInvoiceById, deleteLicenseById } = useLicenseHook(q_queryKey, queryStr);

    const tableColumns = [
        {
            accessor: LicenseListField.LICENSE_KEY,
            title: "License Key",
            sortable: true,
            render: (record: LicenseList) => record?.[LicenseListField.LICENSE_KEY] ? <LicenseKeyTitle licenseKey={record?.[LicenseListField.LICENSE_KEY] ?? "--"} linkUrl={`${RouteEnum.LICENSE_DETAILS}?id=${record?.[LicenseListField.PUBLIC_ID]}`} /> : "--",
        },

        {
            accessor: LicenseListField.VALIDITY,
            title: "Validity",
            sortable: true,
            render: (record: LicenseList) => (record?.[LicenseListField.VALIDITY] ?? "0") + " " + (ValidityType?.[record?.[LicenseListField.VALIDITY_TYPE]]?.title ?? "Month") + "(s)",
        },
        {
            accessor: LicenseListField.ISSUE_DATE,
            title: 'Issued Date',
            sortable: true,
            render: (record: LicenseList) => record?.[LicenseListField.ISSUE_DATE] ? DateHelper.getFormattedDate(record?.[LicenseListField.ISSUE_DATE], "dd/MM/yyyy") : "--",
        },
        {
            accessor: LicenseListField.USER_CAPACITY,
            title: "No. Of Users",
            sortable: true,
            render: (record: LicenseList) => record?.[LicenseListField.USER_CAPACITY] ?? "0",
        },
        {
            accessor: LicenseListField.STORAGE_CAPACITY,
            title: "Data Storage",
            sortable: true,
            render: (record: LicenseList) => (record?.[LicenseListField.STORAGE_CAPACITY] ?? "0") + " GB",
        },
        {
            accessor: LicenseListField.EXPIRY_DATE,
            title: 'Expiry Date',
            sortable: true,
            render: (record: LicenseList) => <CustomExpirationDate expiryDate={record?.[LicenseListField.EXPIRY_DATE] ?? null} />
        },
        {
            accessor: LicenseListField.STATUS,
            title: 'License Status',
            sortable: true,
            render: (record: LicenseList) => StatusHelper.getLicenseStatus(record?.[LicenseListField.STATUS] ?? LicenseStatusEnum.ALL)
        },
        {
            accessor: LicenseListField.COMPANY_NAME,
            title: "Issued Company",
            sortable: true,
            render: (record: LicenseList) => record?.[LicenseListField.COMPANY_NAME] ?? "--",
        },
        {
            accessor: 'action',
            title: "Action",
            render: (record: LicenseList) => <>
                {AccessibilityHelper.hasAccess(AdminModules.BILLING_CREATE) && !record?.[LicenseListField.INVOICE_EXISTS] && record?.[LicenseListField.COMPANY_PUBLIC_ID] && <CustomIconButton toolTipName='Generate Invoice'
                    icon={<IconFileDollar />}
                    onClick={async (e: any) => {
                        let generateInvoiceResponse = await generateLicenseInvoiceById(record?.[LicenseListField.PUBLIC_ID]);

                        if(generateInvoiceResponse?.isSuccess) {
                            setgeneratedInvoicePublicId(generateInvoiceResponse?.data?.data?.[DetailsInvoiceField.PUBLIC_ID] ?? null);
                            setgeneratedInvoiceNo(generateInvoiceResponse?.data?.data?.[DetailsInvoiceField.TRANS_NO] ?? null);
                            setopenGeneratedInvoiceModal(true);
                        }
                    }} />}
                <CustomIconButton toolTipName='View'
                    icon={<IconEye />}
                    onClick={(e: any) => {
                        navigate(`${RouteEnum.LICENSE_DETAILS}?id=${record?.[LicenseListField.PUBLIC_ID]}`, { replace: true })
                    }} />
                {AccessibilityHelper.hasAccess(AdminModules.LICENSE_DELETE) && record?.[LicenseListField.STATUS] != LicenseStatusEnum.USED && <CustomIconButton toolTipName='Delete'
                    icon={<IconTrash color='var(--buttonHoverBGDangerColor)' />}
                    onClick={(e: any) => { 
                        setopenDelete(true);
                        settoDeleteItem(record);
                    }} />}
            </>
        },
    ];

    useEffect(() => {
        setSearchParams(QueryBuilderHelper.CleanQueryDictionary(queryStr));
    }, [queryStr]);

    return <TableContainer>
        <CustomListHeader title={RouteHelper.getRouteDescription(RouteEnum.LICENSES)}
            subTitle=""
            headerButtons={<>
                {/*<CustomButton
                    btnType={ButtonType.PRIMARY}
                    title='Buy License (Package)'
                    startIcon={<IconShoppingCart />}
                    // isRoundedCorner={true}
                    onClick={(e: any) => {
                        navigate(`${RouteEnum.BUY_LICENSE_PACKAGE}`, { replace: true });
                    }} />*/}
                {AccessibilityHelper.hasAccess(AdminModules.BUY_LICENSE) && <CustomButton
                    btnType={ButtonType.PRIMARY}
                    title='Buy License'
                    startIcon={<IconShoppingCart />}
                    // isRoundedCorner={true}
                    onClick={(e: any) => {
                        navigate(`${RouteEnum.BUY_LICENSE}`, { replace: true });
                    }} />}
                {AccessibilityHelper.hasAccess(AdminModules.GENERATE_LICENSE) && <CustomButton
                    btnType={ButtonType.PRIMARY}
                    title='Generate License'
                    startIcon={<IconKey />}
                    // isRoundedCorner={true}
                    onClick={(e: any) => {
                        navigate(`${RouteEnum.GENERATE_LICENSE}`, { replace: true });
                    }} />}
            </>}
        />
        <CustomListSearch searchValue={queryStr.searchString ?? ""}
            onChangeSearch={(searchString) => setQueryStr(prev => ({ ...prev, searchString: searchString, pageNumber: 1 }))}
            searchPlaceholder='Search License Key / Issued Company'
            legends={data?.legends}
            onClickLegendItem={(id) => setQueryStr(prev => ({ ...prev, status: id, pageNumber: 1 }))}>

            <FormDateRangePicker name="licenseIssueDate"
                hideLabel
                title=''
                placeholder='Issue Date'
                fieldValue={[queryStr.issueDateFrom, queryStr.issueDateTo] ?? []}
                handleOnChange={(selectedValue: any) => {
                setQueryStr(prev => ({
                    ...prev,
                    // issueDateFrom: selectedValue[0] ?? DateHelper.getFormattedDate(new Date(), "yyyy-MM-dd"),
                    // issueDateTo: selectedValue[1] ?? DateHelper.getFormattedDate(new Date(), "yyyy-MM-dd"),
                    issueDateFrom: selectedValue[0] ?? null,
                    issueDateTo: selectedValue[1] ?? null,
                    pageNumber: 1,
                }));
            }} />

            {(_userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.VENDOR_ADMIN || _userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.SUPER_USER) && <VendorsDropdown name="vendorPublicId"
                hideLabel
                title=''
                placeholder='Vendor'
                fieldValue={queryStr.vendorPublicId}
                isUserVendorsOnly={_userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.VENDOR_ADMIN}
                handleOnChange={(selectedValue: any) => setQueryStr(prev => ({
                    ...prev,
                    vendorPublicId: selectedValue?.id ?? null,
                    pageNumber: 1,
                }))} />}

            <FormDateRangePicker name="licenseExpiryDate"
                hideLabel
                title=''
                placeholder='Expiry Date'
                fieldValue={[queryStr.expiryDateFrom, queryStr.expiryDateTo] ?? []}
                handleOnChange={(selectedValue: any) => {
                setQueryStr(prev => ({
                    ...prev,
                    // expiryDateFrom: selectedValue[0] ?? DateHelper.getFormattedDate(new Date(), "yyyy-MM-dd"),
                    // expiryDateTo: selectedValue[1] ?? DateHelper.getFormattedDate(new Date(), "yyyy-MM-dd"),
                    expiryDateFrom: selectedValue[0] ?? null,
                    expiryDateTo: selectedValue[1] ?? null,
                    pageNumber: 1,
                    vendorPublicId: null,
                }));
            }} />

            <CustomListSearchResetBtns onClickSearch={() => { }}
                onClickReset={() => {
                    setQueryStr(prev => ({
                        ...prev,
                        searchString: '',
                        issueDateFrom: null,
                        issueDateTo: null,
                        expiryDateFrom: null,
                        expiryDateTo: null,
                        pageNumber: 1,
                     }))
                }}/>
        </CustomListSearch>
        <CustomListComponent
            columns={tableColumns}
            fetching={fetchStatus == "fetching"}
            idAccessor={(record: LicenseList) => `license${record?.[LicenseListField.PUBLIC_ID]}`}
            records={data?.data?.list ?? []}
            totalRecords={data?.data?.metaData?.totalCount ?? 0}
            page={queryStr.pageNumber}
            recordsPerPage={queryStr.pageSize}
            defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
            onSortStatusChange={((sort: any) => { setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 })) })}
            setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
            onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))} />

        {openDelete && <CustomDeleteConfirmation
            openDialog={openDelete}
            ItemToDelete={toDeleteItem?.[LicenseListField.LICENSE_KEY] ?? null} // Optional to display name of the item to be deleted
            handleClose={() => {
                setopenDelete(false);
                settoDeleteItem(null);
            }}
            showRemarksField={true}
            deleteItem={async (remarks: any) => {
                if(toDeleteItem?.[LicenseListField.PUBLIC_ID]) {
                    let deleteReponse: any = await deleteLicenseById(toDeleteItem?.[LicenseListField.PUBLIC_ID], remarks);

                    if(deleteReponse?.isSuccess) {
                        setopenDelete(false);
                    }
                }
            }} />
        }

        {openGeneratedInvoiceModal && <GeneratedLicenseInvoiceModal
            openGeneratedInvoiceModal={openGeneratedInvoiceModal}
            setopenGeneratedInvoiceModal={setopenGeneratedInvoiceModal}
            generatedInvoicePublicId={generatedInvoicePublicId}
            setgeneratedInvoicePublicId={setgeneratedInvoicePublicId}
            generatedInvoiceNo={generatedInvoiceNo}
            setgeneratedInvoiceNo={setgeneratedInvoiceNo} />
        }
    </TableContainer>
}
